import { useEffect, useMemo, useRef, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { fetchThreeStepAiPersonas, type ThreeStepAiPersonaRow } from '../api/rooms'
import { bumpMyGamesPlayed } from '../../../lib/api/profile'
import { buildPromptText } from '../api/weekly'
import { PassPlaySetupScreen } from '../../passplay/PassPlaySetupScreen'
import { generatePassplayActions, generatePassplaySteps } from '../../../lib/ai/passplayAi'
import {
  beginRound, castVote, createState, nextReveal, nextRound,
  readyToVote, readyToWriteActions, readyToWriteSteps,
  startPhase1, startWritingSteps, submitActions, submitSteps,
  type ThreeStepPassPlayState,
} from './state'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useT } from '../../../i18n'

const MAX_ACTION_LEN = 60
const MAX_STEP_LEN = 160

interface Props {
  onExit: () => void
}

/**
 * Top-level Phone Pass Play screen for Three Step Solution.
 * Walks through setup → per-player writes → reveal → voting →
 * results, all on this device, all in React state. AI seats fill
 * instantly from local persona templates; when online, Claude Haiku
 * (`passplay-ai`) upgrades their actions/steps before they're shown.
 */
export function ThreeStepPassPlayApp({ onExit }: Props) {
  const t = useT()
  const [aiPool, setAiPool] = useState<ThreeStepAiPersonaRow[] | null>(null)
  const [state, setState] = useState<ThreeStepPassPlayState | null>(null)

  useEffect(() => {
    fetchThreeStepAiPersonas().then(setAiPool).catch(() => setAiPool([]))
  }, [])

  // Count each finished pass-play game once toward the lifetime
  // Games Played stat (local games never touch the server otherwise).
  // The ref re-arms when a new game starts so "play again" counts too.
  const bumpedRef = useRef(false)
  useEffect(() => {
    if (state?.phase === 'results') {
      if (!bumpedRef.current) { bumpedRef.current = true; bumpMyGamesPlayed() }
    } else {
      bumpedRef.current = false
    }
  }, [state?.phase])

  if (!state) {
    if (!aiPool) {
      return (
        <div className="screen items-center justify-center">
          <BackgroundTitle text="Three Step Solution" color="#0EAE93" />
          <p className="text-brand-muted">{t('Loading…')}</p>
        </div>
      )
    }
    return (
      <PassPlaySetupScreen
        title="Three Step Solution"
        primaryButtonClass="btn-three-step"
        aiPool={aiPool.map(a => ({
          id: a.id, name: a.name, avatarEmoji: a.avatar_emoji,
        }))}
        minPlayers={2}
        maxPlayers={6}
        onStart={(players) => setState(createState(players))}
        onBack={onExit}
      />
    )
  }

  return <Game state={state} setState={setState} onExit={onExit} />
}

function Game({
  state, setState, onExit,
}: {
  state: ThreeStepPassPlayState
  setState: React.Dispatch<React.SetStateAction<ThreeStepPassPlayState | null>>
  onExit: () => void
}) {
  const t = useT()
  const currentPlayer = state.players[state.currentPlayerIdx]
  const round = state.rounds[state.currentRound]

  // ── Online AI upgrades (offline: templates play as-is) ──────────────
  // Actions: fire once at game start. Safe to apply until the action
  // pool is snapshotted at phase1_complete.
  const actionsFiredRef = useRef(false)
  useEffect(() => {
    if (actionsFiredRef.current) return
    actionsFiredRef.current = true
    for (const ai of state.players.filter(p => p.kind === 'ai')) {
      void generatePassplayActions({ personaName: ai.name, aiPlayerId: ai.aiPlayerId })
        .then(actions => {
          if (!actions) return
          setState(prev => {
            if (!prev) return prev
            const safe = ['intro', 'pass_to_actions', 'write_actions'].includes(prev.phase)
            if (!safe) return prev
            return {
              ...prev,
              playerActions: { ...prev.playerActions, [ai.id]: { actions } },
            }
          })
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Steps: fire once per round the moment its prompt exists. Safe to
  // apply until that round's reveal starts.
  const stepsFiredForRoundRef = useRef<Set<number>>(new Set())
  useEffect(() => {
    const r = state.rounds[state.currentRound]
    if (!r) return
    if (stepsFiredForRoundRef.current.has(state.currentRound)) return
    stepsFiredForRoundRef.current.add(state.currentRound)
    const roundIdx = state.currentRound
    for (const ai of state.players.filter(p => p.kind === 'ai')) {
      void generatePassplaySteps({
        personaName: ai.name,
        aiPlayerId: ai.aiPlayerId,
        actionA: r.actionA,
        actionB: r.actionB,
      }).then(steps => {
        if (!steps) return
        setState(prev => {
          if (!prev) return prev
          const safe = prev.currentRound === roundIdx
            && ['round_intro', 'pass_to_steps', 'write_steps'].includes(prev.phase)
          if (!safe) return prev
          return {
            ...prev,
            rounds: prev.rounds.map((rr, i) =>
              i === roundIdx
                ? { ...rr, steps: { ...rr.steps, [ai.id]: { steps } } }
                : rr),
          }
        })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentRound, state.rounds.length])

  const subtitle = useMemo(() => {
    if (state.phase === 'intro') return t('Pass play · setup')
    if (state.phase === 'phase1_complete' || state.phase === 'round_intro') return t('Round {n}', { n: state.currentRound + 1 })
    if (state.rounds.length > 0) return t('Round {n} of 3', { n: state.currentRound + 1 })
    return t('Pass play')
  }, [t, state.phase, state.currentRound, state.rounds.length])

  if (state.phase === 'intro') {
    return (
      <PassPlayShell title="Three Step Solution" subtitle={subtitle} onBack={onExit}>
        <CenterCard
          headline={t('Each of you will write 3 actions.')}
          body={t('Things you do, or things that happen to you: normal, weird, or ridiculous. The phone passes between players. Two of those actions get smashed together into one absurd prompt. Then everyone writes 3 steps to answer it. Funniest gets the most votes.')}
          buttonLabel={t('Start')}
          onTap={() => setState(startPhase1(state))}
        />
      </PassPlayShell>
    )
  }

  if (state.phase === 'pass_to_actions' && currentPlayer) {
    return (
      <PassToPlayerSplash
        playerName={currentPlayer.name}
        instruction={t('Write 3 actions: things you do or things that happen to you.')}
        onReady={() => setState(readyToWriteActions(state))}
      />
    )
  }

  if (state.phase === 'write_actions' && currentPlayer) {
    return (
      <ThreeFieldsScreen
        title="Three Step Solution"
        subtitle={t('{name} · 3 actions', { name: currentPlayer.name })}
        labels={[t('Action 1'), t('Action 2'), t('Action 3')]}
        placeholders={[
          t('Something you do, like "making coffee"'),
          t('Or something that happens to you, like "getting stung by a bee"'),
          t('One more. Anything!'),
        ]}
        maxLen={MAX_ACTION_LEN}
        onSubmit={(vals) => setState(submitActions(state, vals))}
        primaryClass="btn-three-step"
        onBack={onExit}
      />
    )
  }

  if (state.phase === 'phase1_complete') {
    return (
      <PassPlayShell title="Three Step Solution" subtitle={t('All actions in')} onBack={onExit}>
        <CenterCard
          headline={t("Everyone's actions are in.")}
          body={t('Two random actions become the prompt. Then everyone writes 3 steps.')}
          buttonLabel={t("See round 1's prompt")}
          onTap={() => setState(beginRound(state))}
        />
      </PassPlayShell>
    )
  }

  if (state.phase === 'round_intro' && round) {
    return (
      <PassPlayShell title="Three Step Solution" subtitle={subtitle} onBack={onExit}>
        <div className="text-center max-w-sm space-y-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-game-three-step-dark">
            {t('Round {n} · the prompt', { n: state.currentRound + 1 })}
          </p>
          <p className="text-xl font-semibold text-brand-text leading-snug">
            {buildPromptText(round.actionA, round.actionB, t)}
          </p>
          <button
            onClick={() => setState(startWritingSteps(state))}
            className="btn-three-step w-full"
          >
            {t("Got it, let's write")}
          </button>
        </div>
      </PassPlayShell>
    )
  }

  if (state.phase === 'pass_to_steps' && currentPlayer && round) {
    return (
      <PassToPlayerSplash
        playerName={currentPlayer.name}
        instruction={t('Write 3 steps to answer:\n{prompt}', { prompt: buildPromptText(round.actionA, round.actionB, t) })}
        onReady={() => setState(readyToWriteSteps(state))}
      />
    )
  }

  if (state.phase === 'write_steps' && currentPlayer && round) {
    return (
      <ThreeFieldsScreen
        title="Three Step Solution"
        subtitle={t('{name} · 3 steps', { name: currentPlayer.name })}
        promptHeader={buildPromptText(round.actionA, round.actionB, t)}
        labels={[t('Step 1'), t('Step 2'), t('Step 3')]}
        placeholders={[
          t("What's the first move?"),
          t('Then what?'),
          t('Stick the landing.'),
        ]}
        maxLen={MAX_STEP_LEN}
        onSubmit={(vals) => setState(submitSteps(state, vals))}
        primaryClass="btn-three-step"
        onBack={onExit}
      />
    )
  }

  if (state.phase === 'reveal' && round) {
    const ordered = state.players
    const author = ordered[state.revealIndex]
    const playerSteps = author ? round.steps[author.id] : null
    return (
      <PassPlayShell title="Three Step Solution" subtitle={t('Round {n} · reveal', { n: state.currentRound + 1 })} onBack={onExit}>
        <div className="w-full max-w-sm space-y-5">
          <p className="text-xs font-semibold uppercase tracking-widest text-game-three-step-dark text-center">
            {t('Solution {n} of {total}', { n: state.revealIndex + 1, total: ordered.length })}
          </p>
          <div className="card p-4 bg-game-three-step-light border-2 border-game-three-step-dark">
            <p className="text-sm text-brand-muted mb-1">{t('The prompt')}</p>
            <p className="text-base font-semibold text-brand-text leading-snug">
              {buildPromptText(round.actionA, round.actionB, t)}
            </p>
          </div>
          <h2 className="text-2xl font-bold text-brand-text text-center">
            {author?.name}
          </h2>
          <ol className="space-y-3">
            {[playerSteps?.steps[0], playerSteps?.steps[1], playerSteps?.steps[2]].map((s, i) => (
              <li key={i} className="card p-3 flex gap-3">
                <span className="font-display font-black text-game-three-step-dark w-6 shrink-0">
                  {i + 1}.
                </span>
                <span className="text-brand-text">{s ?? '…'}</span>
              </li>
            ))}
          </ol>
          <button
            onClick={() => setState(nextReveal(state))}
            className="btn-three-step w-full"
          >
            {state.revealIndex + 1 < ordered.length ? t('Next solution') : t('Start voting')}
          </button>
        </div>
      </PassPlayShell>
    )
  }

  if (state.phase === 'pass_to_vote' && currentPlayer) {
    return (
      <PassToPlayerSplash
        playerName={currentPlayer.name}
        instruction={t("Pick your favorite (you can't vote for yourself).")}
        onReady={() => setState(readyToVote(state))}
      />
    )
  }

  if (state.phase === 'vote' && currentPlayer && round) {
    const choices = state.players.filter(p => p.id !== currentPlayer.id)
    return (
      <PassPlayShell title="Three Step Solution" subtitle={t('{name} · vote', { name: currentPlayer.name })} onBack={onExit}>
        <div className="w-full max-w-sm space-y-3">
          <p className="text-center text-brand-muted text-sm">{t('Tap the solution you liked best.')}</p>
          {choices.map(p => {
            const ps = round.steps[p.id]
            return (
              <button
                key={p.id}
                onClick={() => setState(castVote(state, p.id))}
                className="w-full card p-3 text-left active:scale-[0.98] transition-transform"
              >
                <p className="text-xs font-semibold text-game-three-step-dark mb-1">
                  {p.name}
                </p>
                <ol className="space-y-1 text-sm text-brand-text">
                  <li>1. {ps?.steps[0] ?? '…'}</li>
                  <li>2. {ps?.steps[1] ?? '…'}</li>
                  <li>3. {ps?.steps[2] ?? '…'}</li>
                </ol>
              </button>
            )
          })}
        </div>
      </PassPlayShell>
    )
  }

  if (state.phase === 'between_rounds') {
    const ranked = state.players.slice().sort((a, b) => (state.totalVotes[b.id] ?? 0) - (state.totalVotes[a.id] ?? 0))
    return (
      <PassPlayShell title="Three Step Solution" subtitle={t('After round {n}', { n: state.currentRound + 1 })} onBack={onExit}>
        <div className="w-full max-w-sm space-y-4">
          <h2 className="text-2xl font-bold text-brand-text text-center">{t('Standings')}</h2>
          <ol className="space-y-2">
            {ranked.map((p, i) => {
              const votes = state.totalVotes[p.id] ?? 0
              return (
                <li key={p.id} className="card p-3 flex items-center justify-between">
                  <span className="flex items-center gap-3">
                    <span className="font-display text-2xl font-black text-game-three-step-dark w-6">
                      {i + 1}.
                    </span>
                    <span className="font-semibold text-brand-text">{p.name}</span>
                  </span>
                  <span className="text-sm text-brand-muted">
                    {votes === 1 ? t('1 vote') : t('{n} votes', { n: votes })}
                  </span>
                </li>
              )
            })}
          </ol>
          <button
            onClick={() => setState(nextRound(state))}
            className="btn-three-step w-full"
          >{t('Next round')}</button>
        </div>
      </PassPlayShell>
    )
  }

  if (state.phase === 'results') {
    const ranked = state.players.slice().sort((a, b) => (state.totalVotes[b.id] ?? 0) - (state.totalVotes[a.id] ?? 0))
    const winner = ranked[0]
    const winnerVotes = state.totalVotes[winner?.id ?? ''] ?? 0
    return (
      <PassPlayShell title="Three Step Solution" subtitle={t('Final results')} onBack={onExit}>
        <div className="w-full max-w-sm space-y-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-game-three-step-dark">
            {t('Winner')}
          </p>
          <h1 className="font-display text-5xl font-black text-brand-text">{winner?.name}</h1>
          <p className="text-lg text-brand-muted">
            {winnerVotes === 1 ? t('1 vote') : t('{n} votes', { n: winnerVotes })}
          </p>
          <ol className="space-y-2 text-left mt-6">
            {ranked.map((p, i) => (
              <li key={p.id} className="card p-3 flex items-center justify-between">
                <span className="flex items-center gap-3">
                  <span className="font-display text-2xl font-black text-game-three-step-dark w-6">
                    {i + 1}.
                  </span>
                  <span className="font-semibold text-brand-text">{p.name}</span>
                </span>
                <span className="text-sm text-brand-muted">
                  {state.totalVotes[p.id] ?? 0}
                </span>
              </li>
            ))}
          </ol>

          {/* Every round's prompt + every player's three steps — the
              full story of the game, same as the online end screen. */}
          {state.rounds.length > 0 && (
            <div className="space-y-3 text-left mt-6">
              <p className="text-xs font-semibold text-brand-text uppercase tracking-wide text-center">
                {t('Every round')}
              </p>
              {state.rounds.map((rd, roundIdx) => {
                const entries = state.players
                  .filter(p => rd.steps[p.id])
                  .map(p => ({
                    player: p,
                    steps: rd.steps[p.id]!.steps,
                    votes: Object.values(rd.votes).filter(v => v === p.id).length,
                  }))
                  .sort((a, b) => b.votes - a.votes)
                return (
                  <div key={roundIdx} className="card p-4 space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide">
                        {t('Round {n}', { n: roundIdx + 1 })}
                      </p>
                      <p className="font-display text-lg font-bold text-brand-text leading-snug mt-0.5">
                        {buildPromptText(rd.actionA, rd.actionB, t)}
                      </p>
                    </div>
                    <ul className="space-y-3">
                      {entries.map(({ player, steps, votes }) => (
                        <li key={player.id} className="rounded-xl bg-brand-elevated p-3">
                          <div className="flex items-center justify-between gap-3 mb-1.5">
                            <span className="font-semibold text-brand-text text-sm truncate">
                              {player.name}
                            </span>
                            <span className="text-xs text-brand-muted shrink-0">
                              {votes === 1 ? t('1 vote') : t('{n} votes', { n: votes })}
                            </span>
                          </div>
                          <ol className="space-y-1 text-sm text-brand-text">
                            {steps.map((text, i) => (
                              <li key={i} className="flex gap-1.5">
                                <span className="font-bold text-game-three-step-dark shrink-0">{i + 1}.</span>
                                <span>{text}</span>
                              </li>
                            ))}
                          </ol>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })}
            </div>
          )}

          <button onClick={onExit} className="btn-three-step w-full mt-6">
            {t('Done')}
          </button>
        </div>
      </PassPlayShell>
    )
  }

  return (
    <PassPlayShell title="Three Step Solution" subtitle="…" onBack={onExit}>
      <p className="text-brand-muted">{t('Unexpected state.')}</p>
    </PassPlayShell>
  )
}

// ─── Reusable bits ───────────────────────────────────────────────────

function PassPlayShell({
  title, subtitle, onBack, children,
}: {
  title: string
  subtitle: string
  onBack: () => void
  children: React.ReactNode
}) {
  return (
    <div className="screen">
      <BackgroundTitle text="Three Step Solution" color="#0EAE93" />
      <PageHeader title={title} subtitle={subtitle} onBack={onBack} />
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-6 relative z-10">
        {children}
      </div>
    </div>
  )
}

function CenterCard({
  headline, body, buttonLabel, onTap,
}: {
  headline: string
  body: string
  buttonLabel: string
  onTap: () => void
}) {
  return (
    <div className="text-center max-w-sm space-y-5">
      <h2 className="text-2xl font-bold text-brand-text leading-tight">{headline}</h2>
      <p className="text-brand-muted leading-relaxed">{body}</p>
      <button onClick={onTap} className="btn-three-step w-full">
        {buttonLabel}
      </button>
    </div>
  )
}

function PassToPlayerSplash({
  playerName, instruction, onReady,
}: {
  playerName: string
  instruction: string
  onReady: () => void
}) {
  const t = useT()
  return (
    <div className="screen items-center justify-center bg-game-three-step-light">
      <BackgroundTitle text="Three Step Solution" color="#0EAE93" />
      <div className="text-center max-w-sm px-6 space-y-5">
        <p className="text-xs font-semibold uppercase tracking-widest text-game-three-step-dark">{t('Pass the phone to')}</p>
        <h1 className="font-display text-5xl font-black text-brand-text">{playerName}</h1>
        <p className="text-brand-muted whitespace-pre-line">{instruction}</p>
        <button onClick={onReady} className="btn-three-step w-full mt-4">
          {t("I'm {name}, start", { name: playerName })}
        </button>
      </div>
    </div>
  )
}

function ThreeFieldsScreen({
  title, subtitle, labels, placeholders, maxLen, onSubmit, primaryClass, onBack, promptHeader,
}: {
  title: string
  subtitle: string
  labels: [string, string, string]
  placeholders: [string, string, string]
  maxLen: number
  onSubmit: (vals: [string, string, string]) => void
  primaryClass: string
  onBack: () => void
  promptHeader?: string
}) {
  const t = useT()
  const [a, setA] = useState('')
  const [b, setB] = useState('')
  const [c, setC] = useState('')
  const allFilled = a.trim() && b.trim() && c.trim()
  return (
    <div className="screen">
      <BackgroundTitle text="Three Step Solution" color="#0EAE93" />
      <PageHeader title={title} subtitle={subtitle} onBack={onBack} />
      <div className="flex-1 flex flex-col px-5 py-4 gap-4 overflow-y-auto pb-32 relative z-10">
        {promptHeader && (
          <div className="card p-3 bg-game-three-step-light border-2 border-game-three-step-dark">
            <p className="text-xs text-game-three-step-dark mb-1 uppercase tracking-wide font-semibold">{t('The prompt')}</p>
            <p className="text-base font-semibold text-brand-text leading-snug">
              {promptHeader}
            </p>
          </div>
        )}
        {[
          { label: labels[0], value: a, set: setA, placeholder: placeholders[0] },
          { label: labels[1], value: b, set: setB, placeholder: placeholders[1] },
          { label: labels[2], value: c, set: setC, placeholder: placeholders[2] },
        ].map((f, i) => (
          <label key={i} className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-brand-muted">
              {f.label}
            </span>
            <input
              type="text"
              value={f.value}
              onChange={e => f.set(e.target.value.slice(0, maxLen))}
              placeholder={f.placeholder}
              maxLength={maxLen}
              className="mt-1 w-full px-4 py-3 rounded-2xl border border-brand-border
                         bg-white text-brand-text text-base
                         focus:outline-hidden focus:border-game-three-step-dark
                         focus:ring-2 focus:ring-game-three-step-dark/20"
            />
          </label>
        ))}
      </div>
      <div className="floating-actions">
        <button
          onClick={() => onSubmit([a.trim(), b.trim(), c.trim()])}
          disabled={!allFilled}
          className={`${primaryClass} disabled:opacity-40`}
        >
          {t('Submit')}
        </button>
      </div>
    </div>
  )
}
